import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { useDateFormat } from "@/hooks/useDateFormat";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DeleteLineAdsAccountModal } from "@/pages/applications/pages/lineAdsIntegration/DeleteLineAdsAccountModal";
import { LineAdsLoadError } from "@/pages/applications/pages/lineAdsIntegration/LineAdsLoadError";
import { useLineAdsErrorMessage } from "@/pages/applications/pages/lineAdsIntegration/useLineAdsErrorMessage";

type LineAdsAccount = InterludeTypes["LineAdsAccount"];

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

const styles = {
  groupCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    maxWidth: "512px",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgContainer,
  },
  groupInfo: {
    flex: 1,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const AccessBadge: React.FC<{ hasAccess: boolean | null }> =
  function AccessBadge({ hasAccess }) {
    const { t } = useTranslation();
    return hasAccess === false ? (
      <Badge variant="negative">
        {t("applications.lineAds.disconnected", "Disconnected")}
      </Badge>
    ) : (
      <Badge variant="positive">
        {t("applications.lineAds.connected", "Connected")}
      </Badge>
    );
  };

interface LineAdsAccountRowProps {
  account: LineAdsAccount;
  onDelete: (account: LineAdsAccount) => void;
}

const LineAdsAccountRow: React.FC<LineAdsAccountRowProps> =
  function LineAdsAccountRow({ account, onDelete }) {
    const { t } = useTranslation();
    const dateFormat = useDateFormat({ dateFormatOptions });
    const handleDelete = useHandler(() => onDelete(account));

    return (
      <TableRow>
        <TableCell>
          <Typography
            variant="body"
            fontWeight={500}
            color={theme.colors.staticFgBody}
          >
            {account.name}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {account.externalAccountId}
          </Typography>
        </TableCell>
        <TableCell>
          <AccessBadge hasAccess={account.hasAccess} />
        </TableCell>
        <TableCell>{dateFormat(account.updatedAt)}</TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            aria-label={t(
              "applications.lineAds.accounts.removeAccount",
              "Remove ad account",
            )}
            onClick={handleDelete}
          >
            <MotifIcon un-i-motif="bin" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

export const ListLineAdsAccount: React.FC = function ListLineAdsAccount() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const getLineAdsErrorMessage = useLineAdsErrorMessage();
  const groupQuery = interlude.lineAds.useGetGroup({
    queries: { checkAccess: true },
  });
  const accountsQuery = interlude.lineAds.useListAccounts();
  const accounts = accountsQuery.data?.accounts ?? [];
  const [deletingAccount, setDeletingAccount] = useState<LineAdsAccount | null>(
    null,
  );

  const reloadAccountsMutation = interlude.lineAds.useReloadAccounts(
    undefined,
    {
      onSuccess() {
        enqueueSnackbar(
          t(
            "applications.lineAds.accounts.reloadSuccess",
            "Ad accounts reloaded",
          ),
          { variant: "success" },
        );
      },
      onError(error) {
        const lineAdsError = getLineAdsErrorMessage(error);
        if (lineAdsError) {
          enqueueSnackbar(lineAdsError.message, { variant: "error" });
        }
      },
    },
  );
  // The list refetches on its own when a mutation settles, so none of these
  // callbacks refresh it.
  const deleteAccountMutation = interlude.lineAds.useDeleteAccount(
    { params: { accountId: deletingAccount?.id ?? 0 } },
    {
      onSuccess() {
        setDeletingAccount(null);
        enqueueSnackbar(
          t(
            "applications.lineAds.deleteAccountModal.success",
            "Ad account removed",
          ),
          { variant: "success" },
        );
      },
      onError() {
        setDeletingAccount(null);
      },
    },
  );

  const handleReload = useHandler(() => {
    reloadAccountsMutation.mutate(undefined);
  });
  const handleCloseDelete = useHandler(() => {
    setDeletingAccount(null);
  });
  const handleConfirmDelete = useHandler(() => {
    deleteAccountMutation.mutate(undefined);
  });

  const columns = [
    t("applications.lineAds.accounts.column.account", "Ad account"),
    t("applications.lineAds.accounts.column.status", "Status"),
    t("applications.lineAds.accounts.column.lastUpdated", "Last updated"),
    t("applications.lineAds.accounts.column.actions", "Actions"),
  ];

  return (
    <Stack gap="24px">
      <Stack gap="8px">
        <Typography
          variant="h3"
          fontWeight="medium"
          color={theme.colors.staticFgTitle}
        >
          {t("applications.lineAds.group.title", "LINE Ads group")}
        </Typography>
        {groupQuery.isSuccess ? (
          <Box sx={styles.groupCard}>
            <MotifIcon un-i-motif="line_color" />
            <Stack sx={styles.groupInfo}>
              <Typography variant="body" color={theme.colors.staticFgTitle}>
                {groupQuery.data.externalGroupId}
              </Typography>
            </Stack>
            <AccessBadge hasAccess={groupQuery.data.hasAccess} />
          </Box>
        ) : groupQuery.isPending ? (
          <BarLoading />
        ) : (
          <LineAdsLoadError error={groupQuery.error} />
        )}
      </Stack>
      <Stack gap="16px">
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          gap="16px"
        >
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("applications.lineAds.accounts.title", "Ad accounts")}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t(
                "applications.lineAds.accounts.description",
                "Ad accounts under this group. MAAC segments can be uploaded to them as audiences.",
              )}
            </Typography>
          </Stack>
          <Button
            variant="secondary"
            onClick={handleReload}
            loading={reloadAccountsMutation.isPending}
            disabled={reloadAccountsMutation.isPending}
          >
            <Stack direction="row" gap="4px" alignItems="center">
              <MotifIcon un-i-motif="arrow_sync" />
              {t("applications.lineAds.accounts.reload", "Reload")}
            </Stack>
          </Button>
        </Stack>
        {accountsQuery.isPending ? (
          <BarLoading />
        ) : accountsQuery.isError ? (
          <LineAdsLoadError error={accountsQuery.error} />
        ) : (
          <TableContainer>
            <Table aria-label="line ads accounts table">
              <TableHead>
                <TableRow>
                  {columns.map((label, index) => (
                    <TableCell
                      key={label}
                      align={index === columns.length - 1 ? "right" : "left"}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {accountsQuery.isSuccess && accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      {t(
                        "applications.lineAds.accounts.empty",
                        "No ad accounts under this group",
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <LineAdsAccountRow
                      key={account.id}
                      account={account}
                      onDelete={setDeletingAccount}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
      {deletingAccount ? (
        <DeleteLineAdsAccountModal
          open
          accountName={deletingAccount.name}
          isLoading={deleteAccountMutation.isPending}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
    </Stack>
  );
};
